import React, { useState, useCallback } from 'react';
import { 
  Treemap as RechartsTreemap, 
  ResponsiveContainer, 
  Tooltip 
} from 'recharts';
import { useReport } from '../ReportContext';
import { Legend } from '../Layout';
import { useSeries, useTransformData } from '../hooks';
import { useChartStyles, ChartTooltip, useDrilldownHandler } from './utils';

/**
 * Truncates text to fit within a given width, adding ellipsis if needed.
 * Uses approximate character width calculation.
 */
const truncateText = (text, maxWidth, fontSize) => {
  if (!text) return '';
  // Approximate character width (varies by font, ~0.6 is a reasonable estimate for most fonts)
  const avgCharWidth = fontSize * 0.6;
  const maxChars = Math.floor(maxWidth / avgCharWidth);

  if (text.length <= maxChars) {
    return text;
  }

  // Leave room for "..."
  const truncatedLength = Math.max(0, maxChars - 3);
  return text.substring(0, truncatedLength) + '...';
};

const CustomTreemapContent = ({
  x,
  y,
  width,
  height,
  name,
  count,
  percentage,
  index,
  fill,
  onClick,
  isClickable
}) => {
  const [hovered, setHovered] = useState(false);
  const { classes } = useChartStyles();

  // Add spacing between rectangles (reduce size by 4px on each side)
  const padding = 4;
  const adjustedX = x + padding;
  const adjustedY = y + padding;
  const adjustedWidth = width - (padding * 2);
  const adjustedHeight = height - (padding * 2);

  // Calculate font size based on rectangle size, but make it larger
  const fontSize = Math.min(adjustedWidth / 8, adjustedHeight / 4, 24);

  // Only show text if rectangle is big enough
  const showText = adjustedWidth > 40 && adjustedHeight > 40;
  // Show percentage only for larger rectangles
  const showPercentage = adjustedWidth > 80 && adjustedHeight > 80;

  // Enhanced hover effect
  const hoverScale = 0.97; // Scale down slightly on hover
  const hoverX = hovered ? adjustedX + (adjustedWidth * (1 - hoverScale)) / 2 : adjustedX;
  const hoverY = hovered ? adjustedY + (adjustedHeight * (1 - hoverScale)) / 2 : adjustedY;
  const hoverWidth = hovered ? adjustedWidth * hoverScale : adjustedWidth;
  const hoverHeight = hovered ? adjustedHeight * hoverScale : adjustedHeight;

  // Calculate available width for text (with some padding)
  const textPadding = 10;
  const availableTextWidth = hoverWidth - (textPadding * 2);

  // Truncate name if needed
  const displayName = truncateText(name, availableTextWidth, fontSize);
  const isNameTruncated = displayName !== name;

  return (
    <g>
      {/* SVG title element for native browser tooltip on hover */}
      {isNameTruncated && <title>{name}</title>}
      <rect
        x={hoverX}
        y={hoverY}
        width={hoverWidth}
        height={hoverHeight}
        rx={3}
        ry={3}
        fill={hovered ? `${fill}ee` : fill}
        onClick={() => isClickable && onClick({
          name,
          count,
          percentage,
          fill
        })}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className={isClickable ? classes.clickableRect : ''}
        stroke={hovered ? "#ffffff" : "#fff"}
        strokeWidth={hovered ? 2 : 1}
        style={{ transition: 'all 0.2s ease-in-out', cursor: isClickable ? 'pointer' : 'default' }}
      />
      {showText && (
        <>
          <text
            x={hoverX + hoverWidth / 2}
            y={hoverY + hoverHeight / 2 - (showPercentage ? fontSize : fontSize / 2)}
            textAnchor="middle"
            fill="#fff"
            fontSize={fontSize}
            fontWeight="bold"
            style={{
              pointerEvents: 'none',
              textShadow: '0px 0px 3px rgba(0,0,0,0.7)'
            }}
          >
            {displayName}
          </text>
          <text
            x={hoverX + hoverWidth / 2}
            y={hoverY + hoverHeight / 2 + (showPercentage ? 0 : fontSize)}
            textAnchor="middle"
            fill="#fff"
            fontSize={fontSize}
            style={{
              pointerEvents: 'none',
              textShadow: '0px 0px 3px rgba(0,0,0,0.7)'
            }}
          >
            {count} {showPercentage && `(${percentage}%)`}
          </text>
        </>
      )}
    </g>
  );
};

export const TreeMap = ({ data, config }) => {
  const { classes } = useChartStyles();
  const { isDrilldownEnabled } = useReport();
  const handleRectClick = useDrilldownHandler(config);
  
  // Check if data is in the expected format
  const chartData = useTransformData(data);
  
  const { series, showLegend } = useSeries(config);

  // Currently only supporting single series
  const treemapSeries = series[0];

  // Calculate total for percentages
  const total = chartData.reduce((sum, item) => sum + (item.count || 0), 0);

  // Transform data for treemap format
  const transformedData = chartData.map((item, index) => ({
    ...item,
    value: item.count,
    fill: treemapSeries.getColorByIndex(index),
    total,
    percentage: Math.round((item.count / total) * 100)
  }));

  return (
    <>
      <div className={classes.treeMapStyles} style={{ 
        backgroundColor: '#ffffff',
        display: "flex", 
        flexDirection: "row", 
        alignItems: "center" 
      }}>
        <div style={{ height: "100%", flexGrow: 1 }}>
          <ResponsiveContainer width="100%" height="100%" style={{ backgroundColor: '#ffffff' }}>
            <RechartsTreemap
              data={transformedData}
              dataKey="value"
              nameKey="name"
              aspectRatio={4/3}
              isAnimationActive={false}
              fill="#ffffff"
              content={
                <CustomTreemapContent 
                  onClick={(clickedDataSubset) => {
                    // Only execute click handler if drilldown is enabled
                    if (!isDrilldownEnabled()) return;
                    
                    const fullDataItem = transformedData.find(item => item.name === clickedDataSubset.name);
                    if (fullDataItem) {
                      handleRectClick(fullDataItem, null, treemapSeries);
                    } else {
                      console.warn("Could not find full data item for drilldown:", clickedDataSubset);
                    }
                  }}
                  isClickable={isDrilldownEnabled()}
                />
              }
            >
              <Tooltip content={<ChartTooltip legendItems={series} chartType="treemap" />} />
            </RechartsTreemap>
          </ResponsiveContainer>
        </div>
        {showLegend() && <Legend items={series} chartType="treemap" position="right" />}
      </div>
    </>
  );
}; 